import React, { useState, useEffect } from 'react';
import QRCode from 'react-qr-code';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Download, Copy, AlertCircle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { BusinessDetails } from '@/types/index';
import { PayrollPeriod } from '@/types/payroll-periods';
import { formatEUR } from '@/lib/payroll';
import { toast } from '@/hooks/ui/use-toast';

interface EmployeeWithPayroll {
  employee_id: string;
  employee_name: string;
  role_name?: string;
  pay_rate: number;
  actual_hours: number;
  gross_pay: number;
  net_pay: number;
  calculation_method: string;
  country_code: string | null;
  auto_calculated: boolean;
  status: string;
  has_payroll_record: boolean;
  payroll_record_id?: string;
  created_at?: string;
}

interface PaymentData {
  amount: number;
  iban: string;
  variableSymbol?: string;
  specificSymbol?: string;
  constantSymbol?: string;
  recipientName?: string;
  paymentDescription?: string;
  dueDate?: Date;
}

interface InlinePayrollQRCodeProps {
  employee: EmployeeWithPayroll;
  business: BusinessDetails | null;
  selectedPeriod?: PayrollPeriod | null;
  userFriendlyState: 'FUTURE' | 'UPCOMING' | 'IN_PROGRESS' | 'TO_BE_PROCESSED' | 'PROCESSED';
  className?: string;
}

export const InlinePayrollQRCode: React.FC<InlinePayrollQRCodeProps> = ({
  employee,
  business,
  selectedPeriod,
  userFriendlyState,
  className
}) => {
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [generating, setGenerating] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Determine if business uses Euro
  const useEuro = business?.country_code === 'SK' || business?.country_code === 'CZ' || business?.country_code === 'AT';
  const formatCurrency = (amount: number) => useEuro ? formatEUR(amount) : `$${amount.toFixed(2)}`;

  // Helper function to get business IBAN with fallback
  const getBusinessIBAN = () => {
    // NOTE: Business table doesn't have IBAN field yet
    // Future: Should use business.business_iban when added to database schema
    // For now, use a placeholder Slovak IBAN that indicates missing data
    return 'SK0000000000000000000000'; // Placeholder - needs real business IBAN
  };

  // Generate payment data
  const createPaymentData = (): PaymentData => {
    return {
      amount: employee.net_pay,
      iban: getBusinessIBAN(),
      variableSymbol: employee.employee_id.substring(0, 8),
      recipientName: employee.employee_name,
      paymentDescription: selectedPeriod ? 
        `Salary ${format(selectedPeriod.date, 'MM/yyyy')}` : 
        `Salary ${format(new Date(), 'MM/yyyy')}`,
      dueDate: new Date()
    };
  };

  // Generate PayBySquare QR code (simplified version for inline display)
  const generateSimplePaymentQR = async (paymentData: PaymentData): Promise<string> => {
    try {
      // For inline display, we'll use a simplified payment URL format
      // In production, this would use the bysquare library for proper PayBySquare generation
      const paymentParams = new URLSearchParams({
        amount: paymentData.amount.toString(),
        iban: paymentData.iban,
        vs: paymentData.variableSymbol || '',
        recipient: paymentData.recipientName || '',
        description: paymentData.paymentDescription || '',
        dueDate: paymentData.dueDate ? format(paymentData.dueDate, 'yyyy-MM-dd') : ''
      });

      // Simple payment URL format (would be PayBySquare in production)
      return `payment://bank?${paymentParams.toString()}`;
    } catch (error) {
      throw new Error('Failed to generate payment QR code');
    }
  };

  // Generate QR code on component mount
  useEffect(() => {
    const generateQR = async () => {
      setGenerating(true);
      setError(null);
      
      try {
        const payment = createPaymentData();
        setPaymentData(payment);
        
        // Generate QR code data
        const qrData = await generateSimplePaymentQR(payment);
        setQrCodeData(qrData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to generate QR code');
      } finally {
        setGenerating(false);
      }
    };

    if (employee.net_pay > 0) {
      generateQR();
    } else {
      setError('No payment amount available');
      setGenerating(false);
    }
  }, [employee, business, selectedPeriod]);

  // Copy QR data to clipboard
  const handleCopyQRData = async () => {
    if (!qrCodeData) return;
    
    try {
      await navigator.clipboard.writeText(qrCodeData);
      setCopied(true);
      toast.success('Payment data copied to clipboard');
      
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy payment data');
    }
  };

  // Download QR code as image
  const handleDownloadQR = () => {
    if (!qrCodeData) return;

    // Create a canvas element to convert QR code to image
    const svg = document.querySelector('.inline-qr-code svg') as SVGElement;
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = `payment-qr-${employee.employee_name.replace(/\s+/g, '-').toLowerCase()}.png`;
      link.href = canvas.toDataURL();
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  if (generating) {
    return (
      <div className="flex items-center justify-center py-4">
        <div className="text-sm text-muted-foreground">Generating QR code...</div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>QR Code Error:</strong> {error}
        </AlertDescription>
      </Alert>
    );
  }

  if (!qrCodeData || !paymentData) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No payment QR code available for this employee.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* QR Code Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium">Payment QR Code</h4>
          <p className="text-xs text-muted-foreground">
            Scan to pay {formatCurrency(paymentData.amount)}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {userFriendlyState === 'IN_PROGRESS' && (
            <Badge className="text-xs">
              Preliminary
            </Badge>
          )}
          {business?.country_code === 'SK' && (
            <Badge variant="outline" className="text-xs">
              PayBySquare
            </Badge>
          )}
        </div>
      </div>

      {/* QR Code Display */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="p-3 bg-white rounded-lg border inline-qr-code">
            <QRCode
              value={qrCodeData}
              size={120}
              level="M"
              bgColor="#FFFFFF"
              fgColor="#000000"
              className="h-auto max-w-full"
            />
          </div>
        </div>
        
        <div className="flex-1 space-y-2">
          {/* Payment Details */}
          <div className="space-y-1 text-xs text-muted-foreground">
            <div><strong>Amount:</strong> {formatCurrency(paymentData.amount)}</div>
            <div><strong>Recipient:</strong> {paymentData.recipientName}</div>
            <div><strong>Reference:</strong> {paymentData.variableSymbol}</div>
            <div><strong>Description:</strong> {paymentData.paymentDescription}</div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyQRData}
              className="text-xs px-2 py-1 h-7"
            >
              {copied ? (
                <CheckCircle className="h-3 w-3 mr-1" />
              ) : (
                <Copy className="h-3 w-3 mr-1" />
              )}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadQR}
              className="text-xs px-2 py-1 h-7"
            >
              <Download className="h-3 w-3 mr-1" />
              Save
            </Button>
          </div>
        </div>
      </div>

      {/* Banking Setup Warning */}
      {paymentData.iban === 'SK0000000000000000000000' && (
        <Alert className="mt-3">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <strong>Setup Required:</strong> QR code uses placeholder IBAN. 
            Add your business banking details in Settings for valid payments.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};